/**
 * @file FilterComponent.js
 * @description Provides filtering functionality with a custom hook and modal component for data filtering operations
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getTierIcon } from '../../utils/utils';
import '../helpers/FilterComponent.css';
import { getText } from '../../i18n';

// Hook for managing filters
export const useFilter = initialData => {
    const [filters, setFilters] = useState({});

    const filteredData = useMemo(() => {
        return initialData.filter(item =>
            Object.entries(filters).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(item[key]),
            ),
        );
    }, [filters, initialData]);

    const applyFilter = (column, values) => {
        setFilters(prevFilters => {
            if (values.length === 0) {
                // If no values are selected, remove the filter for that column
                const { [column]: _, ...remainingFilters } = filters;
                return remainingFilters;
            } else {
                return { ...prevFilters, [column]: values };
            }
        });
    };

    const clearAllFilters = () => {
        setFilters({});
    };

    return [filteredData, applyFilter, clearAllFilters];
};

// Helper to render filter icon, whether its active or not
export const renderFilterIcon = active => {
    return (
        <>
            {active ? (
                <svg
                    style={{
                        marginTop: '4px',
                        display: 'inline-block',
                        width: '1em',
                        height: '1em',
                    }}
                >
                    <path
                        d="M1.08871 2.81875C0.701376 2.38585 0.507709 2.1694 0.500404 1.98544C0.494058 1.82564 0.562731 1.67205 0.686053 1.57022C0.828016 1.453 1.11846 1.453 1.69935 1.453H9.30065C9.88154 1.453 10.172 1.453 10.3139 1.57022C10.4373 1.67205 10.5059 1.82564 10.4996 1.98544C10.4923 2.1694 10.2986 2.38585 9.91129 2.81875L6.98903 6.0848C6.91182 6.17109 6.87322 6.21424 6.84569 6.26335C6.82128 6.3069 6.80336 6.35378 6.79252 6.40252C6.78029 6.45747 6.78029 6.51537 6.78029 6.63116V9.36948C6.78029 9.46963 6.78029 9.5197 6.76414 9.563C6.74986 9.60125 6.72665 9.63554 6.69643 9.663C6.66223 9.69408 6.61574 9.71268 6.52275 9.74987L4.78156 10.4463C4.59333 10.5216 4.49922 10.5593 4.42367 10.5436C4.35761 10.5299 4.29963 10.4906 4.26235 10.4344C4.21971 10.37 4.21971 10.2687 4.21971 10.066V6.63116C4.21971 6.51537 4.21971 6.45747 4.20748 6.40252C4.19664 6.35378 4.17872 6.3069 4.15431 6.26335C4.12678 6.21424 4.08818 6.17109 4.01096 6.0848L1.08871 2.81875Z"
                        fill="#1366FF"
                        stroke="#1366FF"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            ) : (
                <i className="bi bi-funnel" style={{ fontSize: '0.7em' }}></i>
            )}
        </>
    );
};

// Modal component for filtering
export const FilterModal = ({
    column,
    options,
    selectedOptions,
    onApply,
    onClose,
    getString = null,
    isRightColumn = false,
    modalStyle = null,
    displayTierIcon = false,
}) => {
    const [tempSelectedOptions, setTempSelectedOptions] =
        useState(selectedOptions);
    const [searchTerm, setSearchTerm] = useState('');
    const modalRef = useRef(null);
    const initialOrderRef = useRef(null);

    useEffect(() => {
        setTempSelectedOptions(selectedOptions);
    }, [selectedOptions]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [modalRef, onClose]);

    useEffect(() => {
        if (modalStyle?.position === 'fixed') {
            return undefined;
        }

        if (modalRef.current) {
            const modal = modalRef.current;

            if (isRightColumn) {
                modal.style.right = '0px';
                modal.style.left = 'auto';
            } else {
                modal.style.left = '0px';
                modal.style.right = 'auto';
            }
        }

        return undefined;
    }, [isRightColumn, modalStyle]);

    const handleSelectionChange = option => {
        if (option === 'Select All') {
            setTempSelectedOptions(prev =>
                prev.length === options.length ? [] : [...options],
            );
        } else {
            setTempSelectedOptions(prev => {
                const index = prev.indexOf(option);
                if (index > -1) {
                    return prev.filter(item => item !== option);
                } else {
                    return [...prev, option];
                }
            });
        }
    };

    const handleApply = () => {
        onApply(column, tempSelectedOptions);
        onClose();
    };

    const handleClearFilter = () => {
        setTempSelectedOptions([]);
        onApply(column, []);
    };

    const handleSearchChange = event => {
        setSearchTerm(event.target.value);
    };

    const displayOption = option => {
        if (
            (typeof option === 'number' || typeof option === 'boolean') &&
            getString
        ) {
            return getString(option);
        }
        return String(option);
    };

    if (initialOrderRef.current === null) {
        const initiallySelected = options.filter(option =>
            selectedOptions.includes(option),
        );
        const initiallyUnselected = options.filter(
            option => !selectedOptions.includes(option),
        );
        initialOrderRef.current = [
            ...initiallySelected,
            ...initiallyUnselected,
        ];
    }

    const sortedOptions = initialOrderRef.current.filter(option => {
        const displayText = displayOption(option);
        return (
            displayText &&
            displayText.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    const isAllSelected =
        options.length > 0 && tempSelectedOptions.length === options.length;

    return (
        <div
            ref={modalRef}
            onClick={event => event.stopPropagation()}
            className="filterModal"
            style={modalStyle || undefined}
        >
            <div className="filterSearchbar">
                <i className="bi bi-search" />
                <input
                    onChange={handleSearchChange}
                    type="text"
                    placeholder={getText('msg_bce06414177f')}
                />
            </div>
            <div className="filterButtonWrapper">
                <button
                    className="filterModalButton filterModalButtonUnderline float-start"
                    onClick={handleClearFilter}
                    disabled={tempSelectedOptions.length === 0}
                >
                    {getText('msg_f641dcde74d7')}
                </button>
            </div>
            <hr className="filterDivider" />
            <div className="filterOptionContainer">
                {searchTerm.length <= 0 && (
                    <div className="filterOption">
                        <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={() => handleSelectionChange('Select All')}
                        />
                        <label>{getText('msg_86a599ef4df2')}</label>
                    </div>
                )}

                {sortedOptions.map((option, index) => (
                    <div key={index} className="filterOption">
                        <input
                            type="checkbox"
                            id={`option-${index}`}
                            checked={tempSelectedOptions.includes(option)}
                            onChange={() => handleSelectionChange(option)}
                        />
                        <label htmlFor={`option-${index}`}>
                            {displayOption(option)}
                        </label>
                        {displayTierIcon && (
                            <div style={{ marginLeft: '4px' }}>
                                {getTierIcon(option)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <hr className="filterDivider" />
            <div className="filterButtonWrapper">
                <button
                    className="filterModalButton filterModalButtonBold float-end"
                    onClick={handleApply}
                >
                    {getText('msg_cfea419c3b4e')}
                </button>
            </div>
        </div>
    );
};
